/**
 * Supabase Client Configuration
 * Browser-side client for interacting with Supabase
 *
 * The session lives in HttpOnly `sb-<ref>-auth-token` cookies shared with
 * the server-side middleware (middleware.ts). The browser cannot read them,
 * so this client keeps the session in memory, loads it from
 * `/api/auth/session` and sends Supabase Auth calls through the
 * `/api/auth/gotrue` proxy (pentest retest #817). See `./browser-session`.
 */

import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';
import { getSupabaseAuthCookieName } from './cookie-name';
import { createProxiedAuthFetch, createServerSessionStorage } from './browser-session';

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || '';
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '';

// Only throw error in browser context, not during build
if (typeof window !== 'undefined' && (!supabaseUrl || !supabaseAnonKey)) {
  console.error('Missing Supabase environment variables');
}

const resolvedSupabaseUrl = supabaseUrl || 'https://placeholder.supabase.co';
const storageKey = getSupabaseAuthCookieName();
const isBrowser = typeof window !== 'undefined';

export const supabase = createClient<Database>(
  resolvedSupabaseUrl,
  supabaseAnonKey || 'placeholder-key',
  {
    global: { fetch: createProxiedAuthFetch(resolvedSupabaseUrl) },
    auth: {
      flowType: 'pkce',
      storageKey,
      storage: createServerSessionStorage(storageKey),
      persistSession: true,
      autoRefreshToken: isBrowser,
      detectSessionInUrl: isBrowser,
    },
  }
);

/**
 * gotrue-js serialises every `supabase.auth.*` call through a single
 * Web-Lock named `lock:sb-<ref>-auth-token`. When two of those calls
 * overlap during page mount (e.g. AuthProvider's `getSession()` racing a
 * page-level `setSession()`/`getUser()` on `/auth/accept-invite` or
 * `/auth/callback-client`), the lower-priority operation rejects with
 *
 *   "Lock 'lock:sb-<ref>-auth-token' was released because another
 *    request stole it"
 *
 * The condition is transient — the lock is free once the stealing call
 * finishes — so a single retry after a tiny delay is sufficient. Use
 * this wrapper around any client-side auth call that might race the
 * AuthProvider on first render.
 */
export function isAuthLockStolenError(err: unknown): boolean {
  const message =
    err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : (err as { message?: string } | null)?.message ?? '';
  return /released because another request stole it/i.test(message);
}

export async function withAuthLockRetry<T>(
  operation: () => Promise<T>,
  { retryDelayMs = 50 }: { retryDelayMs?: number } = {},
): Promise<T> {
  try {
    return await operation();
  } catch (err) {
    if (!isAuthLockStolenError(err)) throw err;
    await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
    return await operation();
  }
}

